import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Chip, Paper, Alert, Divider } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobe, faPlus, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { getClientProfile, updateAllowedDomains } from '../../services/api';
import { toast } from 'react-toastify';
import Loader from '../common/Loader';

interface AllowedDomainsSettingsProps {
  clientId: string;
}

const AllowedDomainsSettings: React.FC<AllowedDomainsSettingsProps> = ({ clientId }) => {
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, [clientId]);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const profileData = await getClientProfile(clientId);
      setAllowedDomains(profileData.allowed_domains || []);
      setWebsiteUrl(profileData.website_url || '');
    } catch (error) {
      console.error('Error fetching client profile:', error);
      toast.error('Failed to load allowed domains settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDomain = () => {
    if (!newDomain) return;
    
    // Basic validation
    let domain = newDomain.trim();
    if (!domain.match(/^https?:\/\//)) {
      domain = `https://${domain}`;
    }
    
    // Remove trailing slash if present
    if (domain.endsWith('/')) {
      domain = domain.slice(0, -1);
    }
    
    // Check if domain already exists
    if (allowedDomains.includes(domain)) {
      setError('This domain is already in the list');
      return;
    }
    
    setAllowedDomains([...allowedDomains, domain]);
    setNewDomain('');
    setError('');
  };

  const handleDeleteDomain = (domainToDelete: string) => {
    // Check if domain is the website URL
    if (domainToDelete === websiteUrl) {
      setError('You cannot remove your primary website URL');
      return;
    }
    
    setAllowedDomains(allowedDomains.filter(domain => domain !== domainToDelete));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateAllowedDomains(clientId, allowedDomains);
      toast.success('Allowed domains updated successfully');
    } catch (error) {
      console.error('Error updating allowed domains:', error);
      toast.error('Failed to update allowed domains');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <Loader text="Loading domains settings..." />;
  }

  return (
    <Box>
      <Typography variant="h6" className="mb-4">
        <FontAwesomeIcon icon={faGlobe} className="mr-2" /> Allowed Domains
      </Typography>
      
      <Paper elevation={0} className="p-4 mb-4 bg-brand-50">
        <Typography variant="body2" className="flex items-start">
          <FontAwesomeIcon icon={faInfoCircle} className="text-brand-500 mr-2 mt-1" />
          <span>
            Configure which websites can embed your chat widget. Your primary website URL 
            is automatically included. Add additional domains if you want to embed the 
            widget on other sites.
          </span>
        </Typography>
      </Paper>
      
      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <Box className="mb-6">
        <Typography variant="subtitle1" className="mb-2">Primary Website</Typography>
        <Chip 
          label={websiteUrl || 'No website configured'} 
          color="primary" 
          className="mr-2 mb-2"
        />
        <Typography variant="caption" className="block text-ink-dim mt-1">
          Your primary website is automatically allowed to embed the widget.
        </Typography>
      </Box>
      
      <Divider className="my-4" />
      
      <Box className="mb-4">
        <Typography variant="subtitle1" className="mb-2">Additional Allowed Domains</Typography>
        <Box className="flex flex-wrap gap-2 mb-4">
          {allowedDomains
            .filter(domain => domain !== websiteUrl)
            .map((domain, index) => (
              <Chip
                key={index}
                label={domain}
                onDelete={() => handleDeleteDomain(domain)}
                className="mb-2"
              />
            ))}
          {allowedDomains.filter(domain => domain !== websiteUrl).length === 0 && (
            <Typography variant="body2" className="text-ink-dim">
              No additional domains configured
            </Typography>
          )}
        </Box>
      </Box>
      
      <Box className="flex gap-2 mb-4">
        <TextField
          label="Add domain (e.g., https://example.com)"
          variant="outlined"
          fullWidth
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="https://example.com"
          size="small"
        />
        <Button 
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          variant="contained" 
          onClick={handleAddDomain}
          disabled={!newDomain}
        >
          Add
        </Button>
      </Box>
      
      <Button
        variant="contained"
        color="primary"
        onClick={handleSave}
        disabled={isSaving}
        className="mt-2"
      >
        {isSaving ? 'Saving...' : 'Save Changes'}
      </Button>
    </Box>
  );
};

export default AllowedDomainsSettings; 